import { useMemo } from 'react';

import { TypedPolicy } from '@/react/portainer/policies/types';
import { useDockerRegistryInitialValues } from '@/react/portainer/policies/components/docker/registry/useDockerRegistryInitialValues';
import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';

import { FormSection } from '@@/form-components/FormSection';
import { InlineLoader } from '@@/InlineLoader';
import { TextTip } from '@@/Tip/TextTip';

import { PolicyTableWrapper } from './PolicyTableWrapper';

interface Props {
  policy: TypedPolicy<unknown>;
}

export function DockerRegistryPolicyReadOnly({ policy }: Props) {
  const values = useDockerRegistryInitialValues(policy);
  const accessRows = values.accessRows || [];

  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });
  const usersQuery = useUsers(false);
  const teamsQuery = useTeams();

  const registryMap = useMemo(
    () => new Map((registriesQuery.data || []).map((r) => [r.Id, r.Name])),
    [registriesQuery.data]
  );
  const userMap = useMemo(
    () => new Map((usersQuery.data || []).map((u) => [u.Id, u.Username])),
    [usersQuery.data]
  );
  const teamMap = useMemo(
    () => new Map((teamsQuery.data || []).map((t) => [t.Id, t.Name])),
    [teamsQuery.data]
  );

  if (accessRows.length === 0) {
    return (
      <FormSection title="Registry Access">
        <TextTip>No registry access configured for this policy.</TextTip>
      </FormSection>
    );
  }

  if (
    registriesQuery.isLoading ||
    usersQuery.isLoading ||
    teamsQuery.isLoading
  ) {
    return (
      <FormSection title="Registry Access">
        <InlineLoader>Loading registry access details...</InlineLoader>
      </FormSection>
    );
  }

  if (registriesQuery.isError || usersQuery.isError || teamsQuery.isError) {
    return (
      <FormSection title="Registry Access">
        <TextTip color="red">
          Failed to load registry access details. Please try again.
        </TextTip>
      </FormSection>
    );
  }

  return (
    <FormSection title="Registry Access">
      <PolicyTableWrapper
        caption="Docker registry access list for this policy"
        data-cy="docker-registry-policy-readonly-table"
      >
        <thead className="bg-gray-3 th-dark:bg-gray-9">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">
              Registry
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium">Users</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Teams</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-5 th-dark:divide-gray-7">
          {accessRows.map((row) => (
            <tr key={row.registryId}>
              <td className="px-4 py-3 text-sm font-medium">
                {registryMap.get(row.registryId) || 'Unknown'}
              </td>
              <td className="px-4 py-3 text-sm">
                {row.userIds.length > 0
                  ? row.userIds
                      .map((id) => userMap.get(id) || `User ${id}`)
                      .join(', ')
                  : 'None'}
              </td>
              <td className="px-4 py-3 text-sm">
                {row.teamIds.length > 0
                  ? row.teamIds
                      .map((id) => teamMap.get(id) || `Team ${id}`)
                      .join(', ')
                  : 'None'}
              </td>
            </tr>
          ))}
        </tbody>
      </PolicyTableWrapper>
    </FormSection>
  );
}
